import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import useRewardState, { IReward } from '@/states/reward.state';
import useTenantState from '@/states/tenant.state';
import useOrgChartState from '@/states/organization.state';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { useGetOrgChartNames } from '@/pages/company-profile/sop/useSopMedia';
import { buildRewardFields } from '@/components/company/reward/rewardFields';
import { useRefetchCompletionStatus } from './useCompletionStatus';

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export const useRewardLogic = () => {
  const formControl = useForm<IReward>({
    defaultValues: {
      bonus_name: '',
      bonus_period: '',
      bonus_target: '',
      bonus_amount: '',
      is_published: false,
      category: '',
    },
  });
  const { reset } = formControl;
  const formRef = useRef<HTMLFormElement>(null);
  const refetchCompletionStatus = useRefetchCompletionStatus();

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms debounce
  const [allPublished, setAllPublished] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [formMode, setFormMode] = useState<'add' | 'edit' | null>(null);
  const [selectedRow, setSelectedRow] = useState<IReward | null>(null);
  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'bonus_name',
    order: 'asc',
  });

  const { currentTenant } = useTenantState();
  const { orgCharts, fetchOrgChartTree } = useOrgChartState();
  const { rewards, paginatedData, getRewards, createReward, updateReward, deleteReward, bulkPublishReward, isLoading } =
    useRewardState();

  // The "Jabatan" select (shown only for the "level_jabatan" category) is sourced
  // from the positions defined in the organization structure.
  const watchedCategory = useWatch({ control: formControl.control, name: 'category' });
  const { data: orgChartNames = [] } = useGetOrgChartNames(currentTenant?.id);
  const jabatanOptions = orgChartNames.map(node => ({ label: node.name, value: String(node.id) }));

  // Fetch org chart on mount
  useEffect(() => {
    if (!currentTenant?.id) return;
    fetchOrgChartTree(currentTenant.id);
  }, [currentTenant?.id, fetchOrgChartTree]);

  // Fetch data with server-side pagination and filters
  const fetchData = useCallback(async () => {
    if (!currentTenant?.id) return;

    try {
      await getRewards(currentTenant.id, {
        page: pageConfig.page,
        page_size: pageConfig.limit,
        order: pageConfig.order,
        order_by: pageConfig.orderBy,
        search: debouncedSearchTerm || undefined,
        published: selectedStatus === 'Published' ? true : selectedStatus === 'Unpublished' ? false : undefined,
      });
    } catch {
      toast.error('Failed to fetch rewards');
    }
  }, [
    currentTenant?.id,
    pageConfig.page,
    pageConfig.limit,
    pageConfig.order,
    pageConfig.orderBy,
    debouncedSearchTerm,
    selectedStatus,
    getRewards,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (rewards.length > 0) {
      setAllPublished(rewards.every(item => item.is_published === true));
    } else {
      setAllPublished(false);
    }
  }, [rewards]);

  const handleSave = async (data: IReward) => {
    if (!currentTenant?.id) return;
    try {
      const payload: Partial<IReward> = {
        bonus_name: data.bonus_name?.trim(),
        bonus_period: data.bonus_period?.trim() ?? '',
        bonus_target: data.bonus_target?.trim() ?? '',
        bonus_amount: data.bonus_amount?.trim() ?? '',
        is_published: data.is_published ?? false,
        category: data.category,
        org_chart_node_id:
          data.category === 'level_jabatan' && data.org_chart_node_id ? Number(data.org_chart_node_id) : undefined,
      };

      if (formMode === 'edit' && selectedRow?.id) {
        await updateReward(currentTenant.id, selectedRow.id, payload);
      } else {
        await createReward(currentTenant.id, payload);
      }

      setFormMode(null);
      setSelectedRow(null);
      reset({
        bonus_name: '',
        bonus_period: '',
        bonus_target: '',
        bonus_amount: '',
        is_published: false,
        category: '',
        org_chart_node_id: undefined,
      });
      await fetchData();
      await refetchCompletionStatus();
    } catch {
      toast.error('Failed to save reward');
    }
  };

  const handleDelete = async (row: IReward) => {
    if (!currentTenant?.id || !row.id) return;
    await deleteReward(currentTenant.id, row.id);
    await fetchData();
    await refetchCompletionStatus();
  };

  const fields = buildRewardFields(watchedCategory, jabatanOptions);

  const bulkPublishRewardHandler = async (value: boolean) => {
    if (!currentTenant?.id) return;
    await bulkPublishReward(currentTenant.id, value);
    await fetchData();
  };

  const onEdit = (row: IReward) => {
    const bonusTargetStr =
      typeof row.bonus_target === 'string'
        ? row.bonus_target
        : typeof (row.bonus_target as unknown) === 'number'
        ? String(row.bonus_target)
        : typeof row.value === 'string'
        ? row.value
        : '';
    const bonusAmountStr =
      typeof row.bonus_amount === 'string'
        ? row.bonus_amount
        : typeof (row.bonus_amount as unknown) === 'number'
        ? String(row.bonus_amount)
        : '';

    reset({
      ...row,
      bonus_name: row.bonus_name ?? row.name ?? '',
      bonus_period: row.bonus_period ?? '',
      bonus_target: bonusTargetStr,
      bonus_amount: bonusAmountStr,
      is_published: row.is_published ?? false,
      category: row.category ?? '',
    });

    setFormMode('edit');
    setSelectedRow(row);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedStatus('');
  };

  return {
    isLoading,
    allPublished,
    paginatedData,
    formControl,
    formRef,
    formMode,
    setFormMode,
    handleSave,
    handleDelete,
    reset,
    onEdit,
    pageConfig,
    setPageConfig,
    bulkPublishRewardHandler,
    searchTerm,
    setSearchTerm,
    selectedStatus,
    setSelectedStatus,
    clearFilters,
    orgCharts,
    fields,
    fetchData,
  };
};
